// Period reach, deduplicated by each platform itself for exactly the selected
// dates (ingest/period_reach.py behind /oauth/reports/reach). Reach counts unique
// people and cannot be added up over days or campaigns; the daily rows only
// feed the day-by-day chart.
import { supabase } from "@/lib/supabase";
import type { ActivityFilter, ActivityType, Region } from "@/lib/api/types";

export interface ReachRow {
  platform: string;
  brand_id: string;
  region: Region;
  activity: ActivityFilter;
  /** null: the platform reports no reach for this period (see note). */
  reach: number | null;
  /** false: a sum the platform could not deduplicate (see note). */
  exact: boolean;
  note: string | null;
}

export interface ReachReport {
  status: "ready" | "pending";
  rows?: ReachRow[];
  errors?: string[];
  fetched_at?: string;
}

export async function getPeriodReach(from: string, to: string): Promise<ReachReport> {
  const { data } = await supabase.auth.getSession();
  if (!data.session) throw new Error("Log opnieuw in om het bereik te laden.");
  const response = await fetch(`/oauth/reports/reach?${new URLSearchParams({ from, to })}`, {
    headers: { Authorization: `Bearer ${data.session.access_token}` },
  });
  if (!response.ok) throw new Error("Het bereik kon niet bij de platformen worden opgehaald.");
  return response.json();
}

/** Additive awareness counters per platform × brand × market × activity. */
export interface AwarenessFact {
  platform: string;
  brand_id: string;
  region: Region;
  activity_type: ActivityType;
  impressions: number;
  engagements: number;
  video_views: number;
}

export async function getAwarenessFacts(from: string, to: string): Promise<AwarenessFact[]> {
  const { data, error } = await supabase.rpc("rpc_awareness_facts", { p_from: from, p_to: to });
  if (error) throw error;
  return (data ?? []).map((r: Record<string, unknown>) => ({
    platform: r.platform as string,
    brand_id: r.brand_id as string,
    region: r.region as Region,
    activity_type: r.activity_type as ActivityType,
    impressions: Number(r.impressions ?? 0),
    engagements: Number(r.engagements ?? 0),
    video_views: Number(r.video_views ?? 0),
  }));
}
